<template>
    <asli-slideout id="slideout-more-info" v-model="showMoreInfo">
        <asli-quote-info
            v-if="quoteId && mappedQuote"
            site="Home"
            :quote="mappedQuote"
            :proposal="proposalStore.proposal"
            :isPayMonthly="isPayMonthly"
            :showAnnualAndMonthlyCosts="showAnnualAndMonthlyCosts"
            :show-broker-section="false"
            :display-static-content-for-premium-breakdown="true"
        >
            <asli-details-box
                heading="Your Address"
                :sub-heading="addressDetails"
                :icon="addressMeta.icon"
                :add-padding-for-long-address="true"
                is-home
                show-icons
                parent="address"
                heading-level="h2"
            >
                <template #further-details>
                    <div class="flex w-full h-full justify-between gap-x-2 xs:gap-x-6 pt-2">
                        <asli-details-box-stat icon="calendarBlack" aria-label="Year property built">{{
                            addressMeta?.year_built
                        }}</asli-details-box-stat>
                        <asli-details-box-stat icon="bed" aria-label="Bedrooms">{{
                            addressMeta?.number_of_bedrooms
                        }}</asli-details-box-stat>
                        <asli-details-box-stat icon="bathroom" aria-label="Bathrooms">{{
                            addressMeta?.number_of_bathrooms
                        }}</asli-details-box-stat>
                        <asli-details-box-stat icon="fire" class="min-w-fit" aria-label="Heating type">
                            {{ getHeatingSystemDisplay(addressMeta?.heating_system ?? "") }}</asli-details-box-stat
                        >
                    </div>
                </template>
                <template #call-to-action>
                    <asli-address-search-display :eircode="address.eircode" />
                </template>
            </asli-details-box>
        </asli-quote-info>
    </asli-slideout>
</template>

<script lang="ts" setup>
import { useQuoteForDisplay } from "@/hooks/useQuoteForDisplay";
import { useProposalStore } from "@/stores/risk/risk";
import { Quote } from "@/stores/risk/types/types";
import {
    AsliSlideout,
    AsliQuoteInfo,
    useModel,
    AsliDetailsBox,
    printAddress,
    HOUSE_TYPE,
    AsliIcon,
    AsliDetailsBoxStat,
    getHeatingSystemDisplay,
    AsliAddressSearchDisplay
} from "@mbs-npm/anpost-ui";
import { computed, ref } from "vue";
import { Address, AddressMeta } from "@/stores/risk/types/drivers/driver-types";
import { useListStore } from "@/stores/lists/lists";
import { getListValueByName } from "@/hooks/getListValueName";
import { getIcon } from "@/hooks/getIcon";

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    quoteId: {
        type: Number,
        required: true
    },
    isPayMonthly: {
        type: Boolean,
        required: true
    },
    showAnnualAndMonthlyCosts: {
        type: Boolean,
        required: false,
        default: false
    }
});

const proposalStore = useProposalStore();
const listStore = useListStore();

const lists = computed(() => listStore?.lists);
const quote = computed(() => proposalStore.getQuoteById(props.quoteId) ?? ({} as Quote));
const storedRisk = proposalStore.proposal.risk;

const address = computed<Partial<Address>>(() => ({
    address_line1: storedRisk.property_details.address.address_line1,
    address_line2: storedRisk.property_details.address.address_line2,
    address_line3: storedRisk.property_details.address.address_line3,
    address_line4: storedRisk.property_details.address.address_line4,
    ecad_id: storedRisk.property_details.address.ecad_id,
    eircode: storedRisk.property_details.address.eircode
}));

const addressMeta = computed<AddressMeta>(() => ({
    icon: getIcon(storedRisk.property_details.type),
    type: getListValueByName("property_type", storedRisk.property_details.type) ?? "",
    year_built: storedRisk.property_details.year_built?.split("-")[0] ?? "",
    number_of_bathrooms: storedRisk.property_details.number_of_bathrooms ?? "",
    number_of_bedrooms: storedRisk.property_details.number_of_bedrooms ?? "",
    heating_system: storedRisk.property_details.heating_system ?? ""
}));

const mappedQuote = computed(() => (quote ? useQuoteForDisplay(quote.value) : null));

const emit = defineEmits<{
    (e: "update:modelValue", val: boolean): void;
}>();

const showMoreInfo = useModel(props, emit);

const addressDetails = computed(() => printAddress(address.value));
</script>
